/**
 * The one place a request leaves the browser.
 *
 * `credentials: 'include'` on every call, because the session is an httpOnly
 * cookie on a different origin in development - omit it and every authenticated
 * request 401s with nothing on screen saying why.
 */
import { toast } from 'sonner';

export const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Per-field messages from the API's Zod filter, when it sent any. */
    readonly fields?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Whether the API answered the last request at all, for ServerDownOverlay.
 *
 * Only a network failure, 502 or 504 flips it: those come from the browser or nginx
 * and mean the API itself is down. A 503 does not - that is an answer about one
 * dependency, and blanking the whole app over it would hide the page that asked.
 * Any other response, error or not, proves the API is up.
 */
const UNREACHABLE_STATUS = new Set([502, 504]);
let apiReachable = true;
const reachabilityListeners = new Set<() => void>();

function setApiReachable(value: boolean): void {
  if (value === apiReachable) return;
  apiReachable = value;
  reachabilityListeners.forEach((listener) => listener());
}

export const apiReachability = {
  get: (): boolean => apiReachable,
  subscribe(listener: () => void): () => void {
    reachabilityListeners.add(listener);
    return () => reachabilityListeners.delete(listener);
  },
};

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // A network failure and a rejected request need different words: one means the
    // API is unreachable, the other means it answered and said no.
    setApiReachable(false);
    throw new ApiError(0, 'Cannot reach the server. It may be restarting.');
  }

  setApiReachable(!UNREACHABLE_STATUS.has(response.status));

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      response.status,
      fieldsMessage(payload?.fields) ?? payload?.message ?? `Request failed (${response.status}).`,
      payload?.fields,
    );
  }

  return payload as T;
}

/**
 * A schema refusal, in words that name the field.
 *
 * The API's Zod filter says only "Some fields were not accepted." and lists the
 * fields beside it. Nothing read the list, so leaving out a project on the
 * expense form, or clearing a number in settings, answered with that sentence and
 * no clue which field. Zod's own wording is for developers - "String must contain
 * at least 1 character(s)" for an empty picker - so the common ones say "missing".
 */
function fieldsMessage(fields?: { path: string; message: string }[]): string | null {
  if (!fields?.length) return null;
  const name = (path: string) => {
    const last = path.split('.').filter((p) => !/^\d+$/.test(p)).pop() ?? path;
    const words = last.replace(/Id$/, '').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
  };
  const missing = /^Required$|at least 1 character|received (null|undefined|nan)|Invalid input/i;
  const parts = fields.map((f) =>
    missing.test(f.message) ? `${name(f.path)} is missing` : `${name(f.path)}: ${f.message}`,
  );
  return [...new Set(parts)].map((p) => p.replace(/\.$/, '')).join('. ') + '.';
}

/**
 * onError for an action whose button has no message area of its own - a row
 * action in a table, a delete in a confirm dialog that has already closed. Without
 * one, a refused approve or delete simply did nothing on screen.
 */
export const toastFailure = (what: string) => (error: unknown) => {
  // While the API is down ServerDownOverlay already says so, over the whole app; a
  // toast per failed click under it would only repeat that.
  if (!apiReachable) return;
  toast.error(`${what}: ${error instanceof ApiError ? error.message : 'something went wrong.'}`);
};

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  // PUT where the resource is replaced wholesale rather than merged - a working
  // contract, of which there is exactly one per person.
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  // body is optional - most deletes address a resource entirely by its URL, but
  // dismissal-restore addresses a *set* of event ids, which has no single URL.
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};
